import { useState, useEffect } from "react";
import { FaBell, FaRegBell } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import axiosInstance from "../utils/axiosInstance";
import { formatDistanceToNow } from "date-fns";
import { CONTROL, EmptyState, PANEL } from "./ui";

const NotificationButton = ({ userId, notifications, setNotifications }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [seenNotificationIds, setSeenNotificationIds] = useState(new Set());

  const unseenCount =
    notifications?.filter((n) => !seenNotificationIds.has(n.id)).length || 0;

  const markNotificationsAsRead = async () => {
    try {
      setNotifications([]);
      await axiosInstance.put(`/notifications/users/${userId}/mark-read`);
      setSeenNotificationIds(new Set(notifications.map((n) => n.id)));
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  const markSingleNotificationAsRead = async (notificationId) => {
    try {
      await axiosInstance.put(
        `/notifications/users/${userId}/mark-read/${notificationId}`
      );
      setSeenNotificationIds((prev) => new Set(prev).add(notificationId));
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        aria-label="Notifications"
        title="Notifications"
        className={`relative inline-flex h-10 w-10 items-center justify-center rounded-full border transition hover:border-red-400/60 hover:text-red-500 ${CONTROL}`}
      >
        <FaBell className="h-4 w-4" />
        {unseenCount > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 py-0.5 text-[10px] font-bold text-white ring-2 ring-white dark:ring-[#1c1c1e]">
            {unseenCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={`absolute right-0 z-50 mt-2 w-80 p-4 ${PANEL}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.08em] text-gray-700 dark:text-slate-200">
              <span className="h-4 w-1 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
              Notifications
            </h3>
            {notifications.length > 0 && (
              <button
                onClick={markNotificationsAsRead}
                className="text-xs font-semibold text-red-600 transition hover:text-red-500 dark:text-orange-400"
              >
                Mark all as read
              </button>
            )}
          </div>

          {notifications.length > 0 ? (
            <ul className="max-h-80 space-y-2 overflow-y-auto pr-1">
              {notifications.map((n) => {
                const isRead = seenNotificationIds.has(n.id);
                return (
                  <li
                    key={n.id}
                    className={`relative flex items-start gap-3 rounded-[12px] border p-3 ${
                      isRead
                        ? "border-gray-200 bg-gray-50 dark:border-[#2c2c2e] dark:bg-[#161617]"
                        : "border-red-500/30 bg-gradient-to-br from-red-500/10 to-orange-500/10"
                    }`}
                  >
                    <div className="mt-0.5 text-red-500 dark:text-orange-400">
                      <FaRegBell />
                    </div>
                    <div className="flex flex-col w-full pr-6">
                      <div
                        className="text-sm font-medium text-gray-800 dark:text-slate-100"
                        dangerouslySetInnerHTML={{ __html: n.message }}
                      />
                      {n.url && (
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            await markSingleNotificationAsRead(n.id);
                            window.location.href = n.url;
                          }}
                          className="mt-1 self-start text-xs font-bold uppercase tracking-[0.08em] text-red-600 transition hover:text-red-500 dark:text-orange-400"
                        >
                          View
                        </button>
                      )}
                      {n.date && (
                        <span className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                          {formatDistanceToNow(new Date(n.date), {
                            addSuffix: true,
                          })}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => markSingleNotificationAsRead(n.id)}
                      className="absolute right-2 top-2 text-gray-400 transition hover:text-rose-500"
                      title="Mark as read"
                    >
                      <IoClose size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState title="No new notifications" />
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationButton;
